/**
 * `Expr: ExprApi` — the one expression language of the editor (W1-10c core-expr; contract
 * src/core/expr/types.ts; D§4.11, D§5.8, E§4.9). Used by number fields (single and multi-selection), the
 * Distribute dialog's equivalent expression, Motion path x(t)/y(t)/r(t) and tables (P1).
 *
 * Pipeline: lexer.ts (tokens, units) -> parser.ts (Pratt parser, raw tree) -> check.ts (names per variable set,
 * arities, units, lowering) -> evaluate.ts (seeded, strict evaluation). No eval(), deterministic.
 *
 * Typical use (multi-selection, items in time order):
 *   const p = Expr.parse('lerp(-400, 400, u) * alt(1, -1)', 'noteField');
 *   const rng = Expr.rng(seed);                     // one generator per application
 *   items.forEach((it, i) => Expr.apply(p.value, { v: it.x, i, n, u: n === 1 ? 0 : i / (n - 1), t: it.beat }, rng, tempo));
 *
 * A parsed program is plain data (its lowered form travels with it), so it can be stored or posted to a
 * worker and applied there without re-parsing. A copy whose `src` or `assign` was changed is lowered again from
 * those fields, in the context and field unit it was parsed for. Programs built by hand (only the contract
 * fields) are parsed from `src` on first use in the `table` context (every item variable, `t` is the item's
 * beat, no field unit); a motion path program must come from `Expr.parse(src, 'motionPath')`.
 *
 * Field units: `Expr.parse(src, vars, unit?)` takes the field's display unit as an optional third argument
 * (contract since CR-W1-10c-1), the same as `parseProgram(src, vars, { unit })`.
 * Without it, a result written with s or ms is refused instead of being converted by guess.
 *
 * Additive exports (beyond the contract): `parseProgram` (with the field's unit), `evaluateProgram` (result with
 * the error and its range instead of null), `motionPathScope`, `formatEaseCall`, `exprReference` (help data),
 * `exprVarNames`, `EXPR_MSG` / `EXPR_HELP` (message keys), `SPEED_UNIT_PX_PER_SEC`.
 */

import { createRng } from '../math/rng';
import type { Rng } from '../math/types';
import type { Ease as EaseValue } from '../ease/types';
import type { TempoMap } from '../time/types';
import type { MessageRef, Result } from '../util/types';
import { checkProgram, type ExprParseOptions } from './check';
import { EXPR_HELP, EXPR_MSG, ExprFailure, exprError, type ExprHelpKey } from './errors';
import { run } from './evaluate';
import { FUNCTION_NAMES, FUNCTIONS, signatureOf } from './functions';
import type { Ir } from './ir';
import { normalizeChars } from './lexer';
import { parseRaw, parseRawExpression, type RawProgram } from './parser';
import { printRaw } from './print';
import type { AssignOp, ExprApi, ExprError, ExprProgram, ExprScope, ExprVarSet } from './types';
import { CONSTANTS, SPEED_UNIT_SETS, UNITS, VAR_SETS, varNames } from './vars';

export { EXPR_HELP, EXPR_MSG } from './errors';
export type { ExprHelpKey, ExprMsgKey } from './errors';
export { SPEED_UNIT_PX_PER_SEC } from './ir';
export { easingIdByName, type ExprParseOptions } from './check';

/**
 * A parsed program: the contract fields plus its context and lowered right-hand side (plain data).
 *   constant  for an `=` program whose value does not depend on the item, the random generator or the tempo
 *             map: the value every item gets (a literal such as `12.5` or `50%`, or folded: `2 * pi`,
 *             `ease(4, 0.5)`). Null for every other program, including `+= 10` and `*= 50%`, so a caller can
 *             always use a non-null constant as the new value without looking at `assign`.
 *   vars      the ExprScope fields the program reads, in canonical order: the names used, `v` for compound
 *             assignments, `i` for alt(), `durSec` for the speed unit, `v0` when a speed distance is measured from
 *             the segment start. In motion paths `t` reads `u` and `beat` reads `t` (see motionPathScope).
 */
export interface CompiledProgram extends ExprProgram {
  readonly varSet: ExprVarSet;
  /** Field unit the program was parsed for (ExprParseOptions.unit), or null. */
  readonly unit: string | null;
  readonly rhs: Ir;
  /** Assignment the right-hand side was lowered for. */
  readonly loweredFor: AssignOp;
  /** Source the right-hand side was lowered from. */
  readonly loweredSrc: string;
}

const ASSIGN_OPS: readonly AssignOp[] = ['=', '+=', '-=', '*=', '/='];

function isCompiled(p: ExprProgram): p is CompiledProgram {
  const c = p as {
    readonly varSet?: unknown;
    readonly unit?: unknown;
    readonly rhs?: unknown;
    readonly loweredFor?: unknown;
    readonly loweredSrc?: unknown;
  };
  return (
    typeof c.varSet === 'string' &&
    Object.hasOwn(VAR_SETS, c.varSet) &&
    (c.unit === null || typeof c.unit === 'string') &&
    typeof c.loweredFor === 'string' &&
    typeof c.loweredSrc === 'string' &&
    typeof c.rhs === 'object' &&
    c.rhs !== null &&
    'op' in c.rhs
  );
}

function attempt<T>(run: () => T): Result<T, ExprError> {
  try {
    return { ok: true, value: run() };
  } catch (e) {
    if (e instanceof ExprFailure) return { ok: false, error: e.error };
    throw e;
  }
}

/** Scope used to evaluate item-independent programs once at parse time. */
const CONSTANT_SCOPE: ExprScope = { v: 0, i: 0, n: 1, u: 0 };
const NO_RANDOM: Rng = { seed: 0, next: () => 0 };

function compile(
  src: string,
  raw: RawProgram,
  vars: ExprVarSet,
  options?: ExprParseOptions,
): Result<CompiledProgram, ExprError> {
  const checked = attempt(() => checkProgram(src, raw, vars, options));
  if (!checked.ok) return checked;
  const { rhs, usesRand, pure } = checked.value;
  let constant: number | null = null;
  if (pure) {
    // The right-hand side does not depend on the item: evaluate it now, so constant errors show while typing
    // (for every assignment), and expose it as the program's constant value when it is the new value ('=').
    const value = run(rhs, '=', { scope: CONSTANT_SCOPE, rng: NO_RANDOM, tempo: undefined }, raw.body);
    if (!value.ok) return value;
    if (raw.assign === '/=' && value.value === 0) return { ok: false, error: exprError(EXPR_MSG.divByZero, raw.body) };
    if (raw.assign === '=') constant = value.value;
  }
  const unit = options?.unit;
  return {
    ok: true,
    value: {
      src,
      assign: raw.assign,
      usesRand,
      constant,
      vars: checked.value.vars,
      varSet: vars,
      unit: unit === undefined || unit.trim() === '' ? null : unit,
      rhs,
      loweredFor: raw.assign,
      loweredSrc: src,
    },
  };
}

/**
 * Parses `src` for a context. `options.unit` is the field's display unit, which makes time literals exact
 * (`+= 10ms` on a millisecond field adds 10; on a seconds field 0.01) and accepts the field's own suffix.
 */
export function parseProgram(
  src: string,
  vars: ExprVarSet,
  options?: ExprParseOptions,
): Result<CompiledProgram, ExprError> {
  const raw = parseRaw(src, vars);
  if (!raw.ok) return raw;
  return compile(src, raw.value, vars, options);
}

/** Re-parse cache for programs that arrived without their lowered form (or with a changed src or assignment). */
const reparsed = new WeakMap<ExprProgram, Result<CompiledProgram, ExprError>>();

/** Context in which hand-built programs (only the contract fields) are interpreted. */
const HAND_BUILT_CONTEXT: ExprVarSet = 'table';

function compiledOf(prog: ExprProgram): Result<CompiledProgram, ExprError> {
  // The lowering of `%` and of the speed unit depends on the assignment, so it must be the one parsed, and the
  // lowered form must come from the program's current source.
  if (isCompiled(prog) && prog.loweredFor === prog.assign && prog.loweredSrc === prog.src) {
    return { ok: true, value: prog };
  }
  const cached = reparsed.get(prog);
  if (cached !== undefined) return cached;
  const vars = isCompiled(prog) ? prog.varSet : HAND_BUILT_CONTEXT;
  const options: ExprParseOptions | undefined =
    isCompiled(prog) && prog.unit !== null ? { unit: prog.unit } : undefined;
  const raw = parseRaw(prog.src, vars);
  // The contract fields win: the stored assignment applies.
  const result = raw.ok ? compile(prog.src, { ...raw.value, assign: prog.assign }, vars, options) : raw;
  reparsed.set(prog, result);
  return result;
}

/**
 * Evaluates a program for one item and applies its assignment to `scope.v`. Unlike `Expr.apply`, a failure
 * comes back with its message and the source range to underline (division by zero, missing values, ...).
 */
export function evaluateProgram(
  prog: ExprProgram,
  scope: ExprScope,
  rng: Rng,
  tempo?: TempoMap,
): Result<number, ExprError> {
  const compiled = compiledOf(prog);
  if (!compiled.ok) return compiled;
  const span = { from: 0, to: prog.src.length };
  return run(compiled.value.rhs, prog.assign, { scope, rng, tempo }, span);
}

/** The lexer's white space (after normalizeChars, which maps the Unicode spaces to " "). */
const WS = '[ \\t\\n\\r\\f\\v]*';
const NUM = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)(?:[eE][+-]?\\d+)?';
const PLAIN_RE = new RegExp(`^${WS}(${NUM})${WS}(?:px|%|deg|°)?${WS}$`, 'i');

/**
 * Plain number fast path: an optionally signed decimal number, optionally followed by one of the units that
 * do not change it (`px`, `%`, `deg`, `°`), with or without spaces before the unit, as the lexer reads them.
 * "12.5" -> 12.5, "-3" -> -3, "50%" -> 50, "50 %" -> 50, "45deg" -> 45, "45 °" -> 45, "1e3" -> 1000.
 * Units that convert (`ms`, `s`, `b`, `v`) and everything else return null (the caller parses an expression).
 * Whatever this accepts, `Expr.parse` accepts with the same value.
 */
export function plainNumber(src: string): number | null {
  const s = normalizeChars(src);
  const m = PLAIN_RE.exec(s);
  if (m === null) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value)) return null;
  return value === 0 ? 0 : value;
}

/** Pretty-prints a program from its parts: `format('=', 'lerp(-400,400,u)*alt(1,-1)')` = "= lerp(-400, 400, u) * alt(1, -1)". */
export function formatProgram(assign: AssignOp, body: string): string {
  const op = ASSIGN_OPS.includes(assign) ? assign : '=';
  const raw = parseRawExpression(body);
  const text = raw.ok ? printRaw(raw.value) : body.trim();
  return text === '' ? op : `${op} ${text}`;
}

/**
 * Scope for a motion path sample: `t` in x(t)/y(t)/r(t) is the path progress (0..1, after the time curve),
 * `beat` the chart beat of the sample. Index and count are the sample number and the number of samples.
 */
export function motionPathScope(p: {
  readonly progress: number;
  readonly beat: number;
  readonly sec?: number;
  readonly index?: number;
  readonly count?: number;
}): ExprScope {
  const base = { v: 0, i: p.index ?? 0, n: p.count ?? 1, u: p.progress, t: p.beat };
  return p.sec === undefined ? base : { ...base, s: p.sec };
}

function numText(x: number): string {
  return String(x === 0 ? 0 : x);
}

/**
 * Expression form of a model ease applied to `u` (an expression): Linear (whole curve) -> `u`;
 * a tween -> `ease(id, u)` or `ease(id, u, left, right)` when part of the curve is used; Bezier ->
 * `bezier(x1, y1, x2, y2, u)`; Stay (hold) -> `0` (the curve stays at its start until the next key).
 */
export function formatEaseCall(e: EaseValue, u: string): string {
  switch (e.kind) {
    case 'hold':
      return '0';
    case 'tween': {
      const whole = e.left === 0 && e.right === 1;
      if (e.id === 1 && whole) return u;
      return whole
        ? `ease(${String(e.id)}, ${u})`
        : `ease(${String(e.id)}, ${u}, ${numText(e.left)}, ${numText(e.right)})`;
    }
    case 'bezier':
      return `bezier(${e.p.map(numText).join(', ')}, ${u})`;
  }
}

/** Variable names usable in a context, in display order (constants not included). */
export function exprVarNames(vars: ExprVarSet): readonly string[] {
  return varNames(vars);
}

export interface ExprReferenceEntry {
  /** Name, signature or unit as written in expressions ("u", "lerp(a, b, u)", "deg", "+="). */
  readonly code: string;
  readonly desc: MessageRef;
  /** Example (code, not translated). */
  readonly example?: string;
}

export interface ExprReference {
  readonly assignments: readonly ExprReferenceEntry[];
  readonly variables: readonly ExprReferenceEntry[];
  readonly constants: readonly ExprReferenceEntry[];
  readonly functions: readonly ExprReferenceEntry[];
  readonly units: readonly ExprReferenceEntry[];
}

const ASSIGN_HELP: readonly (readonly [string, ExprHelpKey, string])[] = [
  ['=', EXPR_HELP['assign.set'], '= 100'],
  ['+=', EXPR_HELP['assign.add'], '+= 10'],
  ['-=', EXPR_HELP['assign.sub'], '-= 10'],
  ['*=', EXPR_HELP['assign.mul'], '*= -1'],
  ['/=', EXPR_HELP['assign.div'], '/= 2'],
];

const UNIT_EXAMPLES: Readonly<Record<string, string>> = {
  px: '-135px',
  '%': '*= 50%',
  deg: 'cos(60deg)',
  s: '1.5s',
  ms: '+= 20ms',
  b: 't + 1b',
  v: '3v',
};

/** Help data for a context: what can be typed there, with message refs for the explanations. */
export function exprReference(vars: ExprVarSet): ExprReference {
  return {
    assignments: ASSIGN_HELP.map(([code, key, example]) => ({ code, desc: { key }, example })),
    variables: VAR_SETS[vars].map((v) => ({ code: v.name, desc: { key: v.desc } })),
    constants: CONSTANTS.map((c) => ({ code: c.name, desc: { key: c.desc } })),
    functions: FUNCTION_NAMES.map((name) => {
      const f = FUNCTIONS[name];
      return { code: signatureOf(f), desc: { key: f.desc }, example: f.example };
    }),
    units: UNITS.filter((u) => u.unit !== 'v' || SPEED_UNIT_SETS.has(vars)).map((u) => {
      const example = UNIT_EXAMPLES[u.unit];
      return example === undefined
        ? { code: u.unit, desc: { key: u.desc } }
        : { code: u.unit, desc: { key: u.desc }, example };
    }),
  };
}

export const Expr: ExprApi = {
  // `unit` is optional and beyond the contract (CR-W1-10c-1): callers typed as ExprApi pass two arguments.
  parse: (src: string, vars: ExprVarSet, unit?: string) => parseProgram(src, vars, { unit }),
  apply(prog, scope, rng, tempo) {
    const r = evaluateProgram(prog, scope, rng, tempo);
    return r.ok ? r.value : null;
  },
  plainNumber,
  rng: (seed) => createRng(seed),
  format: formatProgram,
};
